import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthProvider';
import AuthSignIn from './pages/auth/AuthSignIn';
import AuthSignUp from './pages/auth/AuthSignUp';
import ForgotPasswordScreen from './pages/auth/ForgotPasswordScreen';
import VerifyEmail from './pages/auth/VerifyEmail';
import ResetPassword from './pages/auth/ResetPassword';
import NurseDashboard from './pages/nurses/Dashboard';
import NotFound from './pages/not-found/NotFound';
import JobPublicDetails from './pages/job/JobPublicDetails';
import AssignmentPublicDetails from './pages/assignment/AssignmentPublicDetails';
import TimesheetPublicDetails from './pages/timesheet/TimesheetPublicDetails';
import DocumentPublicDetails from './pages/document/DocumentPublicDetails';
import ChatPublicDetails from './pages/chat/ChatPublicDetails';
import AppOpenLanding from './pages/app/AppOpenLanding';
import EmailVerificationMiddleware from './components/EmailVerificationMiddleware';
import TenantRoute from './components/TenantRoute';
import RouteGuard from './components/RouteGuard';

const PrivateRoute = ({ children }) => {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return <Navigate to="/auth/login" />;
  }

  return (
    <EmailVerificationMiddleware>
      {children}
    </EmailVerificationMiddleware>
  );
};

const TenantBasedRedirect = () => {
  const { tenantType } = useAuth();

  // Redirect based on tenant type
  if (tenantType === 'employee') {
    return <Navigate to="/employee" replace />;
  }

  // If no tenant type, redirect to login
  return <Navigate to="/auth/login" replace />;
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <RouteGuard>
          <Routes>
            {/* Public routes */}
            <Route path="/job/:id" element={<JobPublicDetails />} />
            <Route path="/assignment/:id" element={<AssignmentPublicDetails />} />
            <Route path="/timesheet/:id" element={<TimesheetPublicDetails />} />
            <Route path="/document/:id" element={<DocumentPublicDetails />} />
            <Route path="/chat/:id" element={<ChatPublicDetails />} />
            <Route path="/app" element={<AppOpenLanding />} />
            <Route path="/auth/login" element={<AuthSignIn />} />
            {process.env.REACT_APP_SIGNUP_EMPLOYEE_ON === 'true' && (
              <Route path="/auth/signup" element={<AuthSignUp />} />
            )}
            {/* Legacy redirects */}
            <Route path="/login" element={<Navigate to="/auth/login" replace />} />
            <Route path="/signup" element={<Navigate to="/auth/signup" replace />} />
            <Route path="/auth" element={<Navigate to="/auth/login" replace />} />

            <Route path="/auth/employee" element={<AuthSignIn />} />
            {process.env.REACT_APP_SIGNUP_EMPLOYEE_ON === 'true' && (
              <Route path="/auth/employee/signup" element={<AuthSignUp />} />
            )}
            <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Employee routes - only accessible by employee tenant */}
            <Route
              path="/employee/*"
              element={
                <PrivateRoute>
                  <TenantRoute allowedTenants={['employee']}>
                    <NurseDashboard />
                  </TenantRoute>
                </PrivateRoute>
              }
            />

            {/* Root redirect based on tenant */}
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <TenantBasedRedirect />
                </PrivateRoute>
              }
            />

            {/* Catch all - 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </RouteGuard>
      </AuthProvider>
    </Router>
  );
};

export default App;
